import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
} from '@mui/material';
import {
  Science as ScienceIcon,
  MedicalServices as MedicalIcon,
  Biotech as BiotechIcon,
  Psychology as PsychologyIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckIcon,
  People as PeopleIcon,
  MonitorHeart as MonitorHeartIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

const ClinicalHub = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: 'Multimodal Fusion',
      description: 'Combine acoustic, radiology, and pathology data for comprehensive diagnostics.',
      icon: <ScienceIcon sx={{ fontSize: 40 }} />,
      color: '#7C3AED',
      path: '/platform/multimodal-fusion',
    },
    {
      title: 'XAI Visualization',
      description: 'Explainable AI tools to understand model decisions and build trust.',
      icon: <PsychologyIcon sx={{ fontSize: 40 }} />,
      color: '#0EA5E9',
      path: '/diagnostic-view',
    },
    {
      title: 'Clinical Analytics',
      description: 'Real-time performance metrics and patient data processing trends.',
      icon: <AnalyticsIcon sx={{ fontSize: 40 }} />,
      color: '#10B981',
      path: '/analytics',
    },
    {
      title: 'Patient Insights',
      description: 'Comprehensive patient profiles with multi-modal data integration.',
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: '#F59E0B',
      path: '/patients',
    },
    {
      title: 'Diagnostic Core',
      description: 'Advanced diagnostic engine with CTF Workspace architecture.',
      icon: <MedicalIcon sx={{ fontSize: 40 }} />,
      color: '#EF4444',
      path: '/platform/ctf',
    },
    {
      title: 'Precision Medicine',
      description: 'Personalized treatment recommendations based on multimodal analysis.',
      icon: <BiotechIcon sx={{ fontSize: 40 }} />,
      color: '#8B5CF6',
      path: '/solutions/precision-medicine',
    },
  ];

  const stats = [
    { label: 'Models Deployed', value: '12+', icon: <ScienceIcon /> },
    { label: 'Clinical Trials', value: '8', icon: <MedicalIcon /> },
    { label: 'Accuracy Rate', value: '94%', icon: <CheckIcon /> },
    { label: 'Data Sources', value: '3', icon: <MonitorHeartIcon /> },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🏥 Clinical Hub
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Centralized platform for clinical intelligence, multimodal diagnostics, and AI-powered insights
            </Typography>
          </Box>
          <Chip label="Live" color="success" sx={{ fontWeight: 'bold', px: 2 }} />
        </Box>
        <Divider sx={{ mt: 2 }} />
      </Box>

      {/* Callout Banner - Relocated & String Replaced */}
      <div className="bg-gradient-to-r from-indigo-900 to-indigo-800 text-white rounded-xl p-6 mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-lg font-sans">
        <div>
          <h3 className="font-bold text-lg flex items-center gap-2" style={{ margin: 0, color: '#fff' }}>
            🧠 Want to know more on Multimodal AI and Clinical AI Reasoning?
          </h3>
          <p className="text-indigo-200 text-sm mt-1" style={{ margin: 0 }}>
            Explore live transparency logic and manipulate feature layer weights across our 12 operational networks.
          </p>
        </div>
        <Link
          to="/live-diagnostic-case-review"
          className="bg-white text-indigo-900 px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-slate-100 transition shadow-md whitespace-nowrap text-center"
          style={{ textDecoration: 'none' }}
        >
          Multimodal Diagnostic Workspace →
        </Link>
      </div>

      {/* Stats Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: 'primary.main' }}>{stat.icon}</Box>
                <Typography variant="h5" fontWeight="bold">
                  {stat.value}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {stat.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Features Grid */}
      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
        🚀 Platform Features
      </Typography>
      <Grid container spacing={3}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${feature.color}15`,
                    color: feature.color,
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate(feature.path)}
                  sx={{ fontWeight: 'bold' }}
                >
                  Launch
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          ⚡ Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<ScienceIcon />}
              onClick={() => navigate('/platform/ctf')}
              sx={{ py: 1.5 }}
            >
              Run CTF Fusion
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<PsychologyIcon />}
              onClick={() => navigate('/diagnostic-view')}
              sx={{ py: 1.5 }}
            >
              Explore XAI
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AnalyticsIcon />}
              onClick={() => navigate('/analytics')}
              sx={{ py: 1.5 }}
            >
              View Analytics
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Footer Info */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          💡 Clinical Hub integrates multimodal data for comprehensive clinical intelligence
        </Typography>
      </Box>
    </Container>
  );
};

export default ClinicalHub;
